'use client'
import {ReactNode, useState} from 'react'
import {Provider} from 'react-redux'
import {makeStore, AppStore, RootState} from '@/lib/store'
import {authInitialState} from "@/lib/slices/authSlice";
import {cashierInitialState} from "@/lib/slices/cashierSlice";
import {uiInitialState} from "@/lib/slices/uiSlice";
import {getDisplayCurrencyByCountry, isDisplayCurrencyEnabled} from "@/lib/utils/contants/displayCurrencies";
import {parseJwt} from "@/lib/utils";

interface IProps {
    children: ReactNode;
    jwtToken?: string;
    userCurrency?: string;
    isMobileAgent?: boolean;
    country?: string;
}

type Seed = Omit<IProps, 'children'>;

const userIdFromJwt = (jwtToken?: string): string | undefined => {
    if (!jwtToken) return undefined;
    const sub = parseJwt(jwtToken)?.sub;
    return typeof sub === 'string' ? sub : undefined;
}

/**
 * AC-817: the server-derived values are seeded through `preloadedState`, never
 * by dispatching from the render body. A render-phase dispatch re-enters the
 * store while React 19 is rendering and is a prime source of the "reducer is
 * executing" violations (Redux #3/#6) seen on the landing route. The values are
 * still present on the very first render, which is why they were dispatched
 * eagerly before.
 */
const buildPreloadedState = ({jwtToken, userCurrency, country, isMobileAgent}: Seed): Partial<RootState> => ({
    auth: {
        ...authInitialState,
        userId: userIdFromJwt(jwtToken),
        country: country ?? authInitialState.country,
    },
    cashier: {
        ...cashierInitialState,
        // AC-798: explicit cookie choice wins; new visitors default to their
        // country's display currency (GeoIP header), USD otherwise. A cookie
        // holding a currently-disabled display currency (see the
        // ENABLED_DISPLAY_CURRENCIES allow-list) is treated as absent.
        userCurrency: isDisplayCurrencyEnabled(userCurrency) ? userCurrency : getDisplayCurrencyByCountry(country),
    },
    ui: {
        ...uiInitialState,
        isMobile: !!isMobileAgent,
    },
})

export default function StoreProvider({children, jwtToken, userCurrency, country, isMobileAgent}: IProps) {
    // Created exactly once (lazy initializer); never recreated on re-render.
    // AC-655: without `serverState`, react-redux uses the live `store.getState`
    // as the hydration snapshot. A dispatch that lands before a Suspense boundary
    // hydrates (e.g. `authenticate` on /auth/success) then renders that boundary
    // against state the server never saw — a hydration mismatch. Pinning the
    // snapshot to the store's initial state keeps hydration in step with the SSR
    // HTML; React re-renders with the live state right after.
    const [{store, serverState}] = useState(() => {
        const store: AppStore = makeStore(buildPreloadedState({jwtToken, userCurrency, country, isMobileAgent}))
        return {store, serverState: store.getState()}
    })

    return <Provider store={store} serverState={serverState}>{children}</Provider>;
}
